import {ConditionType} from "../../../model2/conditions/condition-types"
import SessionCustomField from "../../../model2/custom-field/session-custom-field"
import Field1ToField2Converter from "../../../fields3/fields-converter-1-2"
import FieldProto from "../../../fields3/proto/field-proto"
import {FieldType} from "../../../fields3/field-type"
import {ProjectSystemType} from "../../../model2/project/types"

// const debugConverter = "filterValue_ConverterToV5"
const debugConverter = false

/**
 * Checks if the given filter value is in the old V1 format.
 * In V5, filter values always have a 'type' property.
 * If 'type' is missing, it's considered a V1 value.
 * 
 * @param initValue - The filter value to check.
 * @returns {boolean} - True if the value is V1 format, false otherwise.
 */
export function isFilterValueV1(initValue: any): boolean {
    return !initValue?.type
}

/**
 * Converts an old-format (V1) filter value to the new V5 format for a given field.
 * Looks up the old field name, extracts the value, deletes it from the input object,
 * and converts it to V5 format if necessary.
 * 
 * @param values - The object containing filter values in old format.
 * @param field - The FieldProto instance for which to extract the value.
 * @returns The converted filter value in V5 format, or null if not found.
 */
export default function initValueForFilterV5_fromOldFormat(values: any, field: FieldProto) {
    // Get the old field name corresponding to this field
    const oldFieldId = getOldFilterFieldName(field)

    debugConverter && console.log(debugConverter, 'fromOldFormat', oldFieldId, values, field)

    // Extract the initial value from the old values object
    let initValue =  _.get(values, oldFieldId, null)
    if (initValue) {
        // Remove the used value so it won't be used for any other entity field
        delete values[oldFieldId]
        // Convert the value to V5 format
        initValue = filterValue_ConverterToV5(initValue, field)
    }

    return initValue
}

/**
 * Maps a FieldProto's type to its corresponding old-format field name.
 * Used to look up values in legacy filter objects.
 * Falls back to the field's fieldId for custom fields.
 * 
 * @param field - The FieldProto instance.
 * @returns {string} - The old-format field name.
 */
function getOldFilterFieldName(field: FieldProto) {
    switch (field.type) {
        case FieldType.TASK_TITLE                   : return 'title'
        case FieldType.TASK_TYPE                    : return 'task-type'
        case FieldType.TASK_STATUS                  : return 'status'
        case FieldType.TASK_SYSTEM_STATUS           : return 'system_status'
        case FieldType.TASK_ACTION_REQUIRED         : return 'action-required'
        case FieldType.TASK_ASSIGNED_TO             : return 'assigned-to'
        case FieldType.TASK_CREATED_BY              : return 'created-by'
        case FieldType.TASK_USERS                   : return 'users'
        case FieldType.TASK_SCHEDULE                : return 'schedule'
        case FieldType.TASK_START_DATE              : return 'start-date'
        case FieldType.TASK_END_DATE                : return 'end-date'
        case FieldType.TASK_MOMENT_CREATED          : return 'date-created'
        case FieldType.TASK_MOMENT_CLOSED           : return 'date-closed'
        case FieldType.TASK_LATEST_ACTIVITY_MOMENT  : return 'last-updated'
        case FieldType.TASK_DEADLINE                : return 'deadline'
        case FieldType.TASK_PRIORITY                : return 'priority'
        case FieldType.TASK_TAGS                    : return 'tags'
        case FieldType.TASK_PROGRESS                : return 'progress-bar'
        case FieldType.TASK_ESTIMATE                : return 'estimate'
        case FieldType.TASK_TIME_REPORTED           : return 'time-reported'
        case FieldType.TASK_PROJECT                 : return 'project'
        case FieldType.TASK_STORY_POINTS            : return 'story-points'

        case FieldType.EVENT_STATUS                 : return 'event-status'
        case FieldType.EVENT_TYPE                   : return 'event-type'

        case FieldType.PROJECT_TYPE                 : return 'project-type'
        case FieldType.PROJECT_HEALTH               : return 'project-health'
        case FieldType.PROJECT_SYSTEM_STATUS        : return 'system_status'
        case FieldType.PROJECT_OWNER                : return 'owner'
        case FieldType.PROJECT_START                : return 'start-date'
        case FieldType.PROJECT_END                  : return 'end-date'
        case FieldType.PROJECT_MOMENT_CREATED       : return 'date-created'
        case FieldType.PROJECT_PROGRESS             : return 'progress-bar'
        case FieldType.PROJECT_STATUS               : return 'status'

        case FieldType.CRM_ACCOUNT                  : return 'crm-account'
        case FieldType.CRM_CONTACTS                 : return 'crm-contacts'

        default:
            /* For custom fields, use the field's fieldId as the key */
            return field.fieldId
    }
}

/**
 * (Function is copied from converter-v1-v4.ts and some changes made)
 * Converts a filter value from the legacy (V1) format to the new V5 format for a given field.
 * Handles both system and custom fields, mapping old filter value structures to the new condition/value format.
 * 
 * @param initValue - The initial filter value (possibly in V1 format)
 * @param field - The FieldProto instance describing the field being filtered
 * @returns The filter value in V5 format (with type and value), or the original value if already V5
 */
function filterValue_ConverterToV5(initValue: any, field: FieldProto) {

    debugConverter && console.log(debugConverter, 'process', field.fieldId, _.cloneDeep(initValue))

    // If the value is already in V5 format, return as is
    if (!isFilterValueV1(initValue)) {
        return initValue
    }

    // Start building the result with the systemFieldId
    let res: any = {
        systemFieldId: field.systemFieldId
    }

    // Get the old-format field name for switch-case mapping
    const oldFieldId = getOldFilterFieldName(field)

    // Main switch: handle all known system fields and their legacy value structures
    switch (oldFieldId) {
        // String "contains" fields (text search)
        case gd.const.fields.type.TITLE:
        // Company user system fields
        case 'name':
        case 'email':
        case 'internal':
        case 'external':
        // CRM contact system fields
        case 'firstName':
        case 'lastName':
        case 'notes':
        case 'phone':
        // CRM account system field
        case 'website':
        // Expenses view system fields
        case 'comments':
            res.type    = ConditionType.STRING_CONTAINS
            res.value   = initValue
            break

        // "Includes" fields (multi-select, reference, etc.)
        case gd.const.fields.type.TASK_TYPE:
        case gd.const.fields.type.STATUS:
        case 'statusId':
        case gd.const.fields.type.SYSTEM_STATUS:
        case gd.const.fields.type.ACTION_REQUIRED:
        case gd.const.fields.type.ASSIGNED_TO:
        case gd.const.fields.type.CREATED_BY:
        case gd.const.fields.type.USERS:
        case gd.const.fields.type.PRIORITY:
        case gd.const.fields.type.PROJECT:
        case gd.const.fields.type.TAGS:
        case gd.const.fields.type.CRM_ACCOUNT:
        case gd.const.fields.type.CRM_CONTACTS:
        // Project system fields
        case gd.const.fields.type.PROJECT_HEALTH:
        case gd.const.fields.type.OWNER:
        // Company user system fields
        case 'companyRole':
        case 'departmentId':
        case 'reportToUserId':
        case 'taskAssignedDeadline':
        case 'eventPastDue':
        // case 'taskARNotScheduled':
        // case 'taskAssignedBehindSchedule':
        // case 'dailyBadgeTimeReport':
        // CRM contact system fields
        case 'crmAccountId':
        case 'contactType':
        case 'contactOwnerUserId':
        // CRM account system field
        case 'accountOwnerUserId':
        // Expenses view system fields
        case 'projectId':
        case 'projectPath':
        case 'userId':
        case 'expenseCategoryId':
        case gd.const.fields.type.STORY_POINTS:
            res.type    = ConditionType.INCLUDES
            res.value   = initValue
            break

        case gd.const.fields.type.PROJECT_TYPE:
            if (initValue.includes(null)) {
                initValue.push(ProjectSystemType.FOLDER)
            }

            res.type    = ConditionType.INCLUDES
            res.value   = _.compact(initValue)
            break

        // Date fields (with options for before/after/range)
        case gd.const.fields.type.SCHEDULE:
        case gd.const.fields.type.START_DATE:
        case gd.const.fields.type.END_DATE:
        case gd.const.fields.type.DATE_CREATED:
        case gd.const.fields.type.DATE_CLOSED:
        case gd.const.fields.type.LAST_UPDATED:
        case gd.const.fields.type.DEADLINE:
        // Expenses view system fields
        case 'expenseDate':
        case 'momentCreated':
            // Handle date filter options
            switch (initValue?.curOption) {
                case 'is-before':
                    res.type    = ConditionType.DATE_FIXED_BEFORE
                    res.value   = initValue.date
                    break

                case 'is-after':
                    res.type    = ConditionType.DATE_FIXED_AFTER
                    res.value   = initValue.date
                    break

                case 'range':
                    res.type    = ConditionType.DATE_FIXED_RANGE
                    res.value   = {
                        start   : initValue.date.start,
                        end     : initValue.date.end
                    }
                    break

                default:
                    break
            }
            break

        // Number fields (with options for greater/less/between)
        case gd.const.fields.type.PROGRESS_BAR:
        case gd.const.fields.type.ESTIMATE:
        case gd.const.fields.type.TIME_REPORTED:
        // Company user system fields
        case 'requiredTimeReportMinutes':
        case 'workdayDuration':
        case 'defaultBreakDuration':
        // Expenses view system fields
        case 'units':
        case 'amount':
            // Handle number filter options
            switch (initValue?.curOption) {
                case 'greater':
                    res.type    = ConditionType.NUMBER_GREATER_THAN
                    res.value   = _.toNumber(initValue.greaterThan)
                    break

                case 'less':
                    res.type    = ConditionType.NUMBER_LESS_THAN
                    res.value   = _.toNumber(initValue.lessThan)
                    break

                case 'between':
                    res.type    = ConditionType.NUMBER_BETWEEN
                    res.value   = {
                        start   : initValue.greaterThan,
                        end     : initValue.lessThan
                    }
                    break

                default:
                    break
            }
            break

        // Boolean fields (single value, usually array with one element)
        case 'isAdmin':
        case 'isActivated':
        case 'guesstimate':
        case 'isDailyBadgeEnabled':
            res.type    = ConditionType.BOOLEAN_EQUALS
            res.value   = initValue?.[0]
            break

        // Custom fields and fallback
        default:
            // Try to resolve the custom field definition from session/company context
            const customFieldV1: SessionCustomField | undefined = gd.session.customFields.get(field.fieldId) || undefined
            let customFieldV3: FieldProto | undefined = customFieldV1 ? Field1ToField2Converter.convert(customFieldV1.id) : undefined

            // Try to find user custom field definition
            const company = gd.session.companies.get(gd.companyId);
            const userCustomFields = company.settings.modules.companyUsers.customFields || [];
            const userCustomField = userCustomFields.find((customField: any) => {
                return customField.fieldId === field.fieldId
            })
            if (!!userCustomField) {
                customFieldV3 = userCustomField
            }

            // Try to find CRM contact custom field definition
            const crmContactCustomFields = company.settings.modules.crmContact.customFields || [];
            const crmContactCustomField = crmContactCustomFields.find((customField: any) => {
                return customField.fieldId === field.fieldId
            })
            if (!!crmContactCustomField) {
                customFieldV3 = crmContactCustomField
            }

            // Try to find CRM account custom field definition
            const crmAccountCustomFields = company.settings.modules.crmAccount.customFields || [];
            const crmAccountCustomField = crmAccountCustomFields.find((customField: any)=> {
                return customField.fieldId === field.fieldId
            })
            if (!!crmAccountCustomField) {
                customFieldV3 = crmAccountCustomField
            }

            // Handle custom field types
            switch (customFieldV3?.type) {
                // String-like custom fields
                case FieldType.STRING:
                case FieldType.TEXT:
                case FieldType.EMAIL:
                case FieldType.PHONE:
                case FieldType.URL:
                case FieldType.ADDRESS:
                    res.type    = ConditionType.STRING_CONTAINS
                    res.value   = initValue
                    break

                // Boolean custom fields
                case FieldType.CHECKBOX:
                case FieldType.TOGGLE:
                    res.type    = ConditionType.BOOLEAN_EQUALS
                    res.value   = initValue?.[0]
                    break

                // Number-like custom fields
                case FieldType.NUMBER:
                case FieldType.CURRENCY:
                case FieldType.PERCENTAGE:
                    switch (initValue?.curOption) {
                        case 'greater':
                            res.type    = ConditionType.NUMBER_GREATER_THAN
                            res.value   = _.toNumber(initValue.greaterThan)
                            break

                        case 'less':
                            res.type    = ConditionType.NUMBER_LESS_THAN
                            res.value   = _.toNumber(initValue.lessThan)
                            break

                        case 'between':
                            res.type    = ConditionType.NUMBER_BETWEEN
                            res.value   = {
                                greaterThan : initValue.greaterThan,
                                lessThan    : initValue.lessThan
                            }
                            break

                        default:
                            break
                    }
                    break

                // Date custom fields
                case FieldType.DATE:
                    switch (initValue?.curOption) {
                        case 'is-before':
                            res.type    = ConditionType.DATE_FIXED_BEFORE
                            res.value   = initValue.date
                            break

                        case 'is-after':
                            res.type    = ConditionType.DATE_FIXED_AFTER
                            res.value   = initValue.date
                            break

                        case 'range':
                            res.type    = ConditionType.DATE_FIXED_RANGE
                            res.value   = {
                                start   : initValue.date.start,
                                end     : initValue.date.end,
                            }
                            break

                        default:
                            break
                    }
                    break

                // Multi-value custom fields (dropdown, tags, users, etc.)
                case FieldType.DROPDOWN:
                case FieldType.MULTISELECT:
                case FieldType.TAGS:
                case FieldType.USER:
                case FieldType.USERS:
                case FieldType.DEPARTMENT:
                case FieldType.SKILLS:
                case FieldType.RAG:
                    res.type    = ConditionType.INCLUDES
                    res.value   = initValue
                    break

                // Special handling for rating fields (convert null to 0 for "Not Set")
                case FieldType.RATING:
                    // In options used (0), in filter value (null). This ensures "Not Set" value appears in filter conditions.
                    const value = initValue?.map((value: number | null)=> {
                        if (_.isNil(value)) {
                            return 0
                        } else {
                            return value
                        }
                    })

                    res.type    = ConditionType.INCLUDES
                    res.value   = value
                    break

                default:
                    // Unknown custom field type: do nothing, return as is
                    break
            }
    }

    // Debug logging for the result
    debugConverter && console.log(debugConverter, 'process res', res)

    // Return the converted filter value in V5 format
    return res
}
